// src/services/scoringService.jsx
import {
  DOCUMENT_TYPE_MAPPING,
  PERSON_TYPE_MAPPING,
  getStandardDocumentName
} from '../constants/scoringConstants';
// Obtener la URL base de la API desde las variables de entorno
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
/**
 * Formatear grupo para GetInfoAvailableScoring con 6 dígitos (padding de ceros a la izquierda)
 * @param {string|number} grupo - Número de grupo
 * @returns {string} Grupo formateado con 6 dígitos
 */
const formatearGrupoAvailable = (grupo) => {
  const grupoStr = String(grupo).trim();
  return grupoStr.padStart(6, '0');
};
/**
 * Formatear orden para GetInfoAvailableScoring con 4 dígitos (padding de ceros a la izquierda)
 * @param {string|number} orden - Número de orden
 * @returns {string} Orden formateada con 4 dígitos
 */
const formatearOrdenAvailable = (orden) => {
  const ordenStr = String(orden).trim();
  return ordenStr.padStart(4, '0');
};
/**
 * Formatear grupo para GetInfoStoredScoring con 4 dígitos (padding de ceros a la izquierda)
 * @param {string|number} grupo - Número de grupo
 * @returns {string} Grupo formateado con 4 dígitos
 */
const formatearGrupoStored = (grupo) => {
  const grupoStr = String(grupo).trim();
  return grupoStr.padStart(4, '0');
};
/**
 * Formatear orden para GetInfoStoredScoring sin padding (valor original)
 * @param {string|number} orden - Número de orden
 * @returns {string} Orden sin formatear
 */
const formatearOrdenStored = (orden) => {
  return String(orden).trim();
};
/**
 * Obtener información de un scoring disponible (no iniciado)
 * @param {string} group - Número de grupo
 * @param {string} order - Número de orden
 * @returns {Promise<Object>} Datos del scoring disponible
 */
export const getInfoAvailableScoring = async (group, order) => {
  try {
    const grupoFormateado = formatearGrupoAvailable(group);
    const ordenFormateada = formatearOrdenAvailable(order);
    const response = await fetch(
      `${API_BASE_URL}/api/Scoring/GetInfoAvailableScoring?grupo=${grupoFormateado}&orden=${ordenFormateada}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al obtener scoring disponible: ${response.status}`);
    }
    const data = await response.json();
    // Verificar si hay un error en la respuesta aunque el status sea 200
    if (data.errMsg) {
      throw new Error(data.errMsg);
    }
    // Verificar si los datos esenciales están presentes
    if (!data.titularData && !data.planData) {
      throw new Error('No se encontraron datos para el grupo y orden especificados');
    }
    return data;
  } catch (error) {
    // No mostrar como error si es un caso esperado (scoring ya procesado)
    if (error.message && error.message.includes('no encontrado')) {
      // Caso esperado, no es un error crítico
    } else {
      console.error('[scoringService] Error en getInfoAvailableScoring:', error);
    }
    throw error;
  }
};
/**
 * Obtener información de un scoring almacenado (ya existente en BD)
 * @param {string} group - Número de grupo
 * @param {string} order - Número de orden
 * @returns {Promise<Object>} Datos del scoring almacenado
 */
export const getInfoStoredScoring = async (group, order, sellerID) => {
  try {
    const grupoFormateado = formatearGrupoStored(group);
    const ordenFormateada = formatearOrdenStored(order);
    const response = await fetch(
      `${API_BASE_URL}/api/Scoring/GetInfoStoredScoring?grupo=${grupoFormateado}&orden=${ordenFormateada}&sellerID=${sellerID}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || `Error al obtener scoring almacenado: ${response.status}`);
    }
    const data = await response.json();
    // Verificar si hay un error en la respuesta aunque el status sea 200
    if (data.errMsg) {
      throw new Error(data.errMsg);
    }
    // Verificar si los datos esenciales están presentes
    if (!data.titularData && !data.planData) {
      throw new Error('No se encontraron datos para el grupo y orden especificados');
    }
    return data;
  } catch (error) {
    console.error('[scoringService] Error en getInfoStoredScoring:', error);
    throw error;
  }
};
/**
 * Validar titular financieramente
 * @param {string} dni - DNI del titular
 * @param {number} quotaValue - Valor de la cuota
 * @param {number} income - Ingresos netos
 * @param {number} debtPercent - Porcentaje de deuda
 * @returns {Promise<Object>} Resultado de la validación del titular
 */
export const validateTitularScoring = async (dni, quotaValue, income, debtPercent) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Scoring/validateTitularScoring`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          dni: dni,
          quotaValue: quotaValue,
          income: income,
          debtPercent: debtPercent
        })
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Error en respuesta de validación titular:', errorData);
      throw new Error(errorData.errMsg || `Error al validar titular: ${response.status}`);
    }
    const result = await response.json();

    // Verificar si el DNI no fue encontrado en la DB
    if (result.result && result.result.includes('DNI no encontrado en la DB')) {
      throw new Error(`DNI ${dni} no encontrado en la base de datos`);
    }

    //MENOR DE EDAD
    if (result.result && result.result.includes('El DNI ingresado pertenece a un menor de edad')) {
      throw new Error(`DNI ${dni} pertenece a un menor de edad`);
    }

    //FALLECIDO
    if (result.result && result.result.includes('El DNI Ingresado pertenece a una persona fallecida')) {
      throw new Error(`DNI ${dni} pertenece a una persona fallecida`);
    }

    return result;
  } catch (error) {
    console.error('Error en validateTitularScoring:', error);
    throw error;
  }
};
/**
 * Validar garante financieramente
 * @param {string} dni - DNI del garante
 * @param {number} quotaValue - Valor de la cuota
 * @param {number} income - Ingresos netos
 * @param {number} debtPercent - Porcentaje de deuda
 * @returns {Promise<Object>} Resultado de la validación del garante
 */
export const validateWarrantorScoring = async (dni, quotaValue, income, debtPercent) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Scoring/validateWarrantorScoring`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          dni: dni,
          quotaValue: quotaValue,
          income: income,
          debtPercent: debtPercent
        })
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Error en respuesta de validación garante:', errorData);
      throw new Error(errorData.errMsg || `Error al validar garante: ${response.status}`);
    }
    const result = await response.json();

    // Verificar si el DNI no fue encontrado en la DB
    if (result.result && result.result.includes('DNI no encontrado en la DB')) {
      throw new Error(`DNI ${dni} no encontrado en la base de datos`);
    }

    
    //MENOR DE EDAD
    if (result.result && result.result.includes('El DNI ingresado pertenece a un menor de edad')) {
      throw new Error(`DNI ${dni} pertenece a un menor de edad`);
    }

    //FALLECIDO
    if (result.result && result.result.includes('El DNI Ingresado pertenece a una persona fallecida')) {
      throw new Error(`DNI ${dni} pertenece a una persona fallecida`);
    }

    return result;
  } catch (error) {
    console.error('Error en validateWarrantorScoring:', error);
    throw error;
  }
};
/**
 * Guardar o actualizar scoring
 * @param {Object} scoringData - Datos del scoring a guardar
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const saveScoring = async (scoringData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Scoring/SaveScoring`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },
      body: JSON.stringify(scoringData),
    });
    if (!response.ok) {
      throw new Error(`Error al guardar scoring: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error en saveScoring:', error);
    throw error;
  }
};
/**
 * Interpretar el resultado de validación del titular
 * @param {string} result - Resultado de la validación
 * @returns {Object} Información parseada sobre garantes y documentos requeridos
 * 
 * Posibles respuestas del endpoint:
 * - "NO APTO"
 * - "APTO"
 * - "1 GARANTE"
 */
export const parseValidationResult = (queryResult) => {
  
  //Break Condition
  if (!queryResult) return { 
    garantesRequeridos: 0, 
    requiereConstancia: false, 
    esApto: true 
  };
  
  //
  const resultUpper = queryResult.result.toUpperCase().trim();
  const debtPercent = queryResult.debtPercent;

  // Caso 1: NO APTO
  if (resultUpper === 'NO APTO') {
    return {
      garantesRequeridos: 0,
      requiereConstancia: false,
      esApto: false,
      mensaje: 'El titular no es apto para el crédito',
    };
  }

  // Caso 2: APTO (sin condiciones adicionales)
  if (resultUpper === 'APTO' && debtPercent <= 0.5) {
    return {
      garantesRequeridos: 0,
      requiereConstancia: false,
      esApto: true,
      mensaje: 'El titular es apto para el crédito',
    };
  }

  // Caso 3: APTO (con condiciones adicionales - Deuda superior al umbral mas bajo)
  if (resultUpper === 'APTO') {
    return {
      garantesRequeridos: 0,
      requiereConstancia: true,
      esApto: true,
      mensaje: 'El titular es apto para el crédito',
    };
  }

  // Case 4: 1 GARANTE (en este caso se pide constancia de ingreso tanto para el titular como el garante)
  if (resultUpper.includes('1 GARANTE')) {
    return {
      garantesRequeridos: 1,
      requiereConstancia: true,
      esApto: true,
      mensaje: 'Se requiere 1 garante',
    };
  }

  // Por defecto: tratar como NO APTO si no reconocemos el formato
  return {
    garantesRequeridos: 0,
    requiereConstancia: false,
    esApto: false,
    mensaje: queryResult.result,
  };
};
/**
 * Guardar scoring incompleto y retornar el ID del scoring
 * @param {Object} scoringData - Datos del scoring incompleto a guardar
 * @returns {Promise<Object>} Respuesta del servidor con el ID del scoring
 */
export const storeIncompleteScoring = async (scoringData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Scoring/StoreIncompleteScoring`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(scoringData),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al guardar scoring incompleto: ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('[scoringService] Error en storeIncompleteScoring:', error);
    throw error;
  }
};
/**
 * Convertir archivo a Base64
 * @param {File} file - Archivo a convertir
 * @returns {Promise<string>} Archivo en formato Base64
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remover el prefijo "data:..." para obtener solo el Base64
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};
/**
 * Subir documento de scoring
 * @param {number} idScoring - ID del scoring
 * @param {File} file - Archivo a subir
 * @param {string} documentType - Tipo de documento (dni, recibos, etc.)
 * @param {string} personType - Tipo de persona (titular, garante1, garante2, etc.)
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const uploadScoringDocument = async (idScoring, file, documentType, personType = 'titular') => {
  try {
    // Convertir archivo a Base64
    const documentB64 = await fileToBase64(file);
    // Obtener nombres estandarizados
    const standardDocType = DOCUMENT_TYPE_MAPPING[documentType] || documentType.toUpperCase();
    const standardPersonSuffix = PERSON_TYPE_MAPPING[personType] || `_${personType.toUpperCase()}`;
    // Extraer la extensión del archivo original
    const fileExtension = file.name.split('.').pop();
    // Crear nombre de documento estandarizado (solo tipo + sufijo + extensión)
    const documentName = getStandardDocumentName(standardDocType, standardPersonSuffix, fileExtension);
    // Preparar el payload según el formato esperado por el backend
    const payload = {
      idScoring: idScoring,
      documentName: documentName,
      documentB64: documentB64
    };
    const url = `${API_BASE_URL}/api/Scoring/uploadScoringDocument`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      let errorData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        const errorText = await response.text();
        errorData = { message: errorText };
      }
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al subir documento: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('[scoringService] Error en uploadScoringDocument:', error);
    throw error;
  }
};
/**
 * Actualizar el estado de un scoring
 * @param {number} idScoring - ID del scoring
 * @param {string} scoringStatus - Nuevo estado del scoring (INCOMPLETO, EN REVISION, EN REVISION SECUNDARIA, RECHAZADO, FINALIZADO APTO, FINALIZADO NO APTO)
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const setScoringStatus = async (idScoring, scoringStatus, statusNotes = '', userID = 0) => {
  try {
    const payload = {
      idScoring: idScoring,
      scoringStatus: scoringStatus,
      statusNotes: statusNotes,
      userID: userID
    };
    const url = `${API_BASE_URL}/api/Scoring/setScoringStatus`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      let errorData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        const errorText = await response.text();
        errorData = { message: errorText };
      }
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al actualizar estado del scoring: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('[scoringService] Error en setScoringStatus:', error);
    throw error;
  }
};
/**
 * Guardar información del garante
 * @param {number} idScoring - ID del scoring
 * @param {Object} warrantorData - Datos del garante
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const storeWarrantorInformation = async (idScoring, warrantorData) => {
  try {
    const payload = {
      idScoring: idScoring,
      warrantorFullName: warrantorData.warrantorFullName || '',
      warrantorDNI: warrantorData.warrantorDNI || '',
      warrantorGender: warrantorData.warrantorGender || '',
      warrantorMarriageStatus: warrantorData.warrantorMarriageStatus || '',
      warrantorIncomeType: warrantorData.warrantorIncomeType || '',
      warrantorIncome: warrantorData.warrantorIncome || '',
      warrantorAddress: warrantorData.warrantorAddress || '',
      warrantorBirthDate: warrantorData.warrantorBirthDate || '',
      warrantorAddressNumber: warrantorData.warrantorAddressNumber || '',
      warrantorAddressFloor: warrantorData.warrantorAddressFloor || '',
      warrantorAddressPostalCode: warrantorData.warrantorAddressPostalCode || '',
      warrantorAddressLocale: warrantorData.warrantorAddressLocale || '',
      warrantorAddressProvince: warrantorData.warrantorAddressProvince || '',
      warrantorPEP: warrantorData.warrantorPEP || 'NO',

      //
      warrantorEmail: warrantorData.warrantorEmail || '',
      warrantorPhone: warrantorData.warrantorPhone || '',
      warrantorCountry: warrantorData.warrantorCountry || '',
      warrantorTaxCondition: warrantorData.warrantorTaxCondition || '',

      // Estructura flat (prioridad) o nested (fallback) para compatibilidad
      warrantorFianceCompleteName: warrantorData.fianceCompleteName || warrantorData.fiance?.completeName || '',
      warrantorFianceDocumentNumber: warrantorData.fianceDocumentNumber || warrantorData.fiance?.documentNumber || '',
      warrantorFianceBirthDate: warrantorData.fianceBirthDate || '',
      warrantorFianceGender: warrantorData.fianceGender || warrantorData.fiance?.gender || '',
      warrantorFianceNationality: warrantorData.fianceNationality || warrantorData.fiance?.nationality || '',
      warrantorFiancePEP: warrantorData.fiancePEP || warrantorData.fiance?.pep || 'NO',
    };
    const url = `${API_BASE_URL}/api/Scoring/storeWarrantorInformation`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      let errorData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        const errorText = await response.text();
        console.error('[scoringService] Error (texto):', errorText);
        errorData = { message: errorText };
      }
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al guardar información del garante: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('[scoringService] Error en storeWarrantorInformation:', error);
    throw error;
  }
};

/**
 * Obtener tracking de eventos de un scoring
 * @param {string} group - Número de grupo
 * @param {string} order - Número de orden
 * @returns {Promise<Array>} Array de eventos del tracking
 */
export const getScoringTracking = async (group, order, sellerID) => {
  try {
    const grupoFormateado = formatearGrupoStored(group);
    const ordenFormateada = formatearOrdenStored(order);

    const response = await fetch(
      `${API_BASE_URL}/api/Scoring/GetScoringTracking?grupo=${grupoFormateado}&orden=${ordenFormateada}&sellerID=${sellerID}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al obtener tracking: ${response.status}`);
    }

    const data = await response.json();

    // Verificar si hay un error en la respuesta aunque el status sea 200
    if (data.errMsg) {
      throw new Error(data.errMsg);
    }

    return data;
  } catch (error) {
    console.error('[scoringService] Error en getScoringTracking:', error);
    throw error;
  }
};

/**
 * Reiniciar un caso de scoring (resetear a estado inicial)
 * @param {string} group - Número de grupo
 * @param {string} order - Número de orden
 * @returns {Promise<Object>} Respuesta del servidor
 */
/**
 * Reiniciar un caso de scoring
 * Endpoint: POST /api/Scoring/resetCase
 * @param {string} group - Número de grupo
 * @param {string} order - Número de orden
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const resetCase = async (group, order, userID) => {
  try {
    // Formatear grupo con 6 dígitos y orden con 4 dígitos
    const grupoFormateado = String(group).trim().padStart(6, '0');
    const ordenFormateada = String(order).trim().padStart(4, '0');

    const response = await fetch(
      `${API_BASE_URL}/api/Scoring/resetCase`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          group: grupoFormateado,
          order: ordenFormateada,
          userID: userID
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[scoringService] Error en respuesta:', errorData);
      throw new Error(errorData.errMsg || errorData.message || `Error al reiniciar caso: ${response.status}`);
    }

    const data = await response.json();

    // Verificar si hay un error en la respuesta aunque el status sea 200
    if (data.errMsg) {
      throw new Error(data.errMsg);
    }

    return data;
  } catch (error) {
    console.error('[scoringService] Error en resetCase:', error);
    throw error;
  }
};
